import React, {useState} from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import loginLogo from '../../images/drtis_background.png';
import {useForm, FormProvider, SubmitHandler} from 'react-hook-form';

import {auth} from '../../firebase';
import {
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {toast} from 'react-toastify';
import isEmail from '../../helpers/isEmail';
import {useNavigate} from 'react-router-dom';

import SignUpDialog from '../SignUpDialog';

interface LoginFormData {
  email: string;
  password: string;
}

const Copyright = (props: any) => {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {'Copyright © '}
      <Link color="inherit" href="https://drtis.com.br/">
        Dr. Tis
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
};

const theme = createTheme();

const Login = () => {
  const methods = useForm<LoginFormData>({
    mode: 'onChange',
  });

  const {
    reset,
    register,
    watch,
    formState: {isSubmitting, errors, isValid},
  } = methods;

  const navigate = useNavigate();

  const [openSignUpDialog, setOpenSignUpDialog] = useState<boolean>(false);

  const [email] = watch(['email']);

  const onSubmit: SubmitHandler<LoginFormData> = (data) => {
    const {email, password} = data;

    signInWithEmailAndPassword(auth, email, password)
        .then((userRecord) => {
          if (!userRecord?.user?.emailVerified) {
            const warnString = 'É necessário verificar o e-mail ' +
            'para acessar. Verifique seu e-mail';

            toast.warn(warnString, {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });

            sendEmailVerification(userRecord.user);

            return auth.signOut();
          }

          return navigate('/home');
        })
        .catch((e) => {
          console.error(e);

          if (e?.code === 'auth/user-not-found') {
            toast.error('Usuário não encontrado!', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });

            reset({
              email: '',
              password: '',
            });
          } else if (e?.code === 'auth/wrong-password') {
            toast.error('Usuário ou senha inválido!', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          } else if (e?.code === 'auth/too-many-requests') {
            toast.warn(
                `Você tentou acessar muitas vezes a conta, 
                ela está temporariamente bloqueada, volte em 15 minutos.`, {
                  position: 'top-right',
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                  progress: undefined,
                });
          }
        });
  };

  const handleResetPassword = () => {
    if (isEmail(email) && email) {
      toast.success('E-mail para redifinição de senha enviado!', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      return sendPasswordResetEmail(auth, email);
    } else {
      toast.error('Preencha o campo e-mail para redefinir a senha.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid container component="main" sx={{height: '100vh'}}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: `url(${loginLogo})`,
            backgroundRepeat: 'no-repeat',
            backgroundColor: (t) =>
              t.palette.mode === 'light' ?
              t.palette.grey[50] :
              t.palette.grey[900],
            backgroundSize: 'auto',
            backgroundPosition: 'center',
          }}
        />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Box
            sx={{
              my: 8,
              mx: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Avatar sx={{m: 1, bgcolor: 'secondary.main'}}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Login
            </Typography>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Box sx={{mt: 1}}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    {
                      ...register('email', {
                        required: true,
                      })
                    }
                    label="E-mail"
                    autoComplete="email"
                    autoFocus
                    error={
                      (
                        errors?.email || (
                          email && (
                            !isEmail(email) ||
                            !email.endsWith('@drtis.com.br')
                          )
                        )
                        ) ?
                      true :
                      false
                    }
                    helperText={<>
                      {
                        errors?.password?.type === 'required' &&
                      'Obrigatório preencher.'
                      }
                      {
                        email &&
                      !isEmail(email) &&
                      'Digite um e-mail válido.'
                      }
                      {
                        email &&
                        !email.endsWith('@drtis.com.br') &&
                        'Apenas e-mails @drtis.com.br podem acessar o sistema.'
                      }
                    </>}
                    disabled={isSubmitting}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    {
                      ...register('password', {
                        required: true,
                        minLength: 6,
                      })
                    }
                    label="Senha"
                    type="password"
                    autoComplete="current-password"
                    error={errors?.password ? true : false}
                    helperText={<>
                      {errors?.password?.type === 'required' &&
                  'Obrigatório preencher.'}
                      {errors?.password?.type === 'minLength' &&
                  'Digite pelo menos 6 caracteres.'}
                    </>}
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{mt: 3, mb: 2}}
                    disabled={isSubmitting || !isValid}
                  >
                Entrar
                  </Button>
                  <Grid container>
                    <Grid item xs={6}>
                      <Link
                        onClick={handleResetPassword}
                        variant="body2">
                    Esqueceu a senha?
                      </Link>
                    </Grid>
                    {/* <Grid item xs={6}>
                      <Link
                        onClick={() => setOpenSignUpDialog(true)}
                        variant="body2"
                      >
                        {'Não tem uma conta? Cadastre-se'}
                      </Link>
                    </Grid> */}
                  </Grid>
                  <Copyright sx={{mt: 5}} />
                </Box>
              </form>
            </FormProvider>
          </Box>
          <SignUpDialog
            open={openSignUpDialog}
            setOpen={setOpenSignUpDialog}
          />
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default Login;
